import * as Sentry from '@sentry/react'

// Everything the check-in scanner does is reported through this module so a
// failing gym can be diagnosed from Sentry alone. Two levels exist:
//
//   - breadcrumbs: always on, free, and attached to whatever event fires next.
//   - events: captured on every failure, and on *every* scan while diagnostics
//     mode is on, so a supervised test session records the happy path too.
//
// Diagnostics mode is off by default on purpose. A busy gym scans a few hundred
// times a day and Sentry's free tier is 5k events/month, so capturing every
// successful scan permanently would burn the quota in a week.

export const SCAN_DEBUG_STORAGE_KEY = 'structural:scan-debug'
export const SCAN_DEBUG_PARAM = 'scanDebug'

export type ScanStage =
  | 'wedge-key'
  | 'wedge-flush'
  | 'wedge-reset'
  | 'wedge-ignored'
  | 'parse'
  | 'camera'
  | 'check-in'

type Level = 'info' | 'warning' | 'error'

function safeStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    // Private mode / blocked cookies throw on access, not on use.
    return null
  }
}

let debugOverride: boolean | null = null

export function isScanDebugEnabled(): boolean {
  if (debugOverride !== null) return debugOverride
  return safeStorage()?.getItem(SCAN_DEBUG_STORAGE_KEY) === '1'
}

export function setScanDebug(on: boolean): void {
  debugOverride = on
  try {
    if (on) safeStorage()?.setItem(SCAN_DEBUG_STORAGE_KEY, '1')
    else safeStorage()?.removeItem(SCAN_DEBUG_STORAGE_KEY)
  } catch {
    // Quota or private mode — the in-memory override still holds for this tab.
  }
}

/**
 * `?scanDebug=1` turns diagnostics on and keeps them on across navigations,
 * `?scanDebug=0` turns them off. Handing a gym a URL is the whole onboarding.
 */
export function syncScanDebugFromUrl(search: string): boolean {
  const value = new URLSearchParams(search).get(SCAN_DEBUG_PARAM)
  if (value === '1' || value === 'true') setScanDebug(true)
  else if (value === '0' || value === 'false') setScanDebug(false)
  return isScanDebugEnabled()
}

function maskChar(c: string): string {
  if (c >= '0' && c <= '9') return '9'
  if (c >= 'a' && c <= 'z') return 'a'
  if (c >= 'A' && c <= 'Z') return 'A'
  if (c === ' ') return '_'
  const cp = c.codePointAt(0) ?? 0
  if (cp < 0x20 || cp === 0x7f) return '.'
  // Punctuation is kept verbatim: a mangled separator is the thing we are
  // hunting, and `/`, `.`, `-` and `:` identify nobody.
  return c
}

export interface PayloadDiagnostics {
  length: number
  /** Char classes with punctuation kept, e.g. `aaaa9.aaaaaaaa/99999999-9999-…`. */
  shape: string
  /** Code points of anything outside the payload's expected alphabet. */
  unexpected: { index: number; char: string; code_point: number }[]
  code_points?: number[]
  raw?: string
}

const EXPECTED_PAYLOAD_CHARS = /^[A-Za-z0-9._:/|-]$/

/**
 * Describes a scanned payload without leaking it by default. `full` is set when
 * the payload already failed to parse (it is garbage, not an identity) or when
 * diagnostics mode is on, and then the raw text and every code point go along.
 */
export function describePayload(raw: string, full = isScanDebugEnabled()): PayloadDiagnostics {
  const chars = Array.from(raw)
  const unexpected = chars
    .map((char, index) => ({ index, char, code_point: char.codePointAt(0) ?? 0 }))
    .filter(({ char }) => !EXPECTED_PAYLOAD_CHARS.test(char))

  return {
    length: chars.length,
    shape: chars.map(maskChar).join(''),
    unexpected,
    ...(full
      ? { raw, code_points: chars.map((c) => c.codePointAt(0) ?? 0) }
      : { code_points: undefined, raw: undefined }),
  }
}

export function scanBreadcrumb(message: string, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({ category: 'scan', level: 'info', message, data })
}

interface ReportOptions {
  stage: ScanStage
  level?: Level
  data?: Record<string, unknown>
  error?: unknown
  /** Capture even when the level is `info` and diagnostics mode is off. */
  force?: boolean
}

/**
 * Reports a scanner event. `info` events only leave a breadcrumb unless
 * diagnostics mode is on; `warning` and `error` are always captured.
 */
export function reportScan(message: string, opts: ReportOptions): void {
  const { stage, level = 'info', data, error, force } = opts

  scanBreadcrumb(message, data)

  if (level === 'info' && !force && !isScanDebugEnabled()) return

  const scope = {
    level,
    tags: { scan_stage: stage, scan_debug: isScanDebugEnabled() ? 'on' : 'off' },
    contexts: { scan: { message, ...data } },
  } as const

  if (error !== undefined) {
    Sentry.captureException(normaliseError(error, message), scope)
    return
  }
  Sentry.captureMessage(`[scan] ${message}`, scope)
}

/**
 * html5-qrcode rejects and throws bare strings, and Sentry groups every bare
 * string under one useless issue. Wrap anything that is not an Error.
 */
export function normaliseError(error: unknown, fallbackMessage: string): Error {
  if (error instanceof Error) return error
  if (typeof error === 'string') return new Error(error)
  return new Error(`${fallbackMessage}: ${JSON.stringify(error)}`)
}

/**
 * The physical-key → character mapping the OS is applying. A hardware scanner
 * emits US scan codes; a Windows box set to a Romanian layout turns some of
 * them into different characters, which is the first thing to rule out when a
 * payload arrives mangled. Chromium only — null everywhere else.
 */
export async function readKeyboardLayout(): Promise<Record<string, string> | null> {
  const keyboard = (
    navigator as Navigator & {
      keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> }
    }
  ).keyboard

  if (!keyboard?.getLayoutMap) return null

  try {
    const map = await keyboard.getLayoutMap()
    const interesting = [
      'Slash',
      'Backslash',
      'Minus',
      'Equal',
      'Period',
      'Comma',
      'Semicolon',
      'Quote',
      'BracketLeft',
      'BracketRight',
      'Backquote',
      'IntlBackslash',
    ]
    const layout: Record<string, string> = {}
    for (const code of interesting) {
      const value = map.get(code)
      if (value !== undefined) layout[code] = value
    }
    return layout
  } catch {
    return null
  }
}
